import React from 'react';
import { arrayOf, bool, func, instanceOf, objectOf, shape, string } from 'prop-types';
import { AccordionDetails, AccordionSummary, Divider, FormGroup, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import {
  GroupSignalCounter,
  SignalsChecklist,
  SignalGroupAccordion,
  SignalGroupsList,
  SignalListItem,
} from './styles';
// may move these utils in the future
import { getSelectedSignalsByGroup, getSelectedSignalCountByGroup } from '../../scenes/Application/components/Webhooks/components/WebhookForm/util';
import SignalCheckbox from './components/SignalCheckbox';
import Checkbox from '../Checkbox';
import Chip from '../Chip';

const SignalSelection = ({
  signals,
  selectedSignals,
  handleSignalClick,
  showSelectedSignalsOnly,
  enableSignalGroupSelection,
  getChipContent,
  columnTitles,

}) => {
  let renderedSignals = {};
  let sortedSignalGroups = Object.keys(signals).sort();

  if (showSelectedSignalsOnly) {
    renderedSignals = getSelectedSignalsByGroup(signals, selectedSignals);
    sortedSignalGroups = Object.keys(renderedSignals).sort();
  } else {
    renderedSignals = signals;
  }

  const getGroupSelectedCount = (signalGroupName) => {
    if (showSelectedSignalsOnly) {
      return `${renderedSignals[signalGroupName].length} selected`;
    }
    const signalCountByGroup = getSelectedSignalCountByGroup(
      signalGroupName, signals, selectedSignals,
    );

    return signalCountByGroup > 0 ? `${signalCountByGroup} selected` : null;
  };

  const getChecklist = (signalGroupName, inAccordion = true) => {
    return (
      <FormGroup>
        <SignalsChecklist inAccordion={inAccordion}>
          {(!showSelectedSignalsOnly && enableSignalGroupSelection) && (
            <li className="select-all">
              <Checkbox
                label={`Select all current and future ${signalGroupName} signals`}
                checked={selectedSignals.has(`${signalGroupName}.*`)}
                onChange={() => handleSignalClick(`${signalGroupName}.*`)}
              />
            </li>
          )}
          {columnTitles && (
            <React.Fragment>
              <SignalListItem hasChip={columnTitles.length > 1} sx={{ pl: 2 }}>
                {columnTitles.map(title => (
                  <Typography
                    key={title}
                    variant="body2"
                    color="text.secondary"
                  >
                    {title}
                  </Typography>
                ))}
              </SignalListItem>
              <Divider sx={{ mt: 1.5, mb: 1.5 }} />
            </React.Fragment>
          )}
          {renderedSignals[signalGroupName].map((signal) => {
            const { name, description, displayName } = signal;
            const signalKey = `${signalGroupName}.${name}`;
            const hasChip = getChipContent && getChipContent(signal);
            return (
              <SignalListItem key={signalKey} hasChip={hasChip}>
                <SignalCheckbox
                  displayName={displayName || signalKey}
                  description={description}
                  signalKey={signalKey}
                  handleSignalClick={handleSignalClick}
                  selectedSignals={selectedSignals}
                />
                {hasChip && (
                  <div className="signal-chip">
                    <Chip label={getChipContent(signal)} />
                  </div>
                )}
              </SignalListItem>
            );
          })}
        </SignalsChecklist>
      </FormGroup>
    );
  };

  return (
    <SignalGroupsList>
      {sortedSignalGroups.map((signalGroupName, i) => {
        const signalGroupCount = getGroupSelectedCount(signalGroupName);
        return (
          <li key={signalGroupName}>
            {sortedSignalGroups.length > 1 || showSelectedSignalsOnly ? (
              <SignalGroupAccordion index={i}>
                <AccordionSummary
                  expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}
                  aria-controls={`content-${signalGroupName}`}
                  id={`header-${signalGroupName}`}
                >
                  <Typography component="span" variant="body2" sx={theme => ({ fontFamily: theme.typography.bold.fontFamily })}>
                    {signalGroupName}
                    {signalGroupCount && (
                      <GroupSignalCounter>
                        {signalGroupCount}
                      </GroupSignalCounter>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  {getChecklist(signalGroupName)}
                </AccordionDetails>
              </SignalGroupAccordion>
            ) : getChecklist(signalGroupName, false)}
          </li>
        );
      })}
    </SignalGroupsList>
  );
};

export default SignalSelection;

SignalSelection.propTypes = {
  signals: objectOf({
    [string]: arrayOf(shape({
      name: string.isRequired,
      groupName: string.isRequired,
      description: string.isRequired,
      permission: string,
      type: string.isRequired,
      signalKey: string, // only used for commands since they're not in the db
    })),
  }),
  selectedSignals: instanceOf(Set).isRequired,
  handleSignalClick: func.isRequired,
  showSelectedSignalsOnly: bool,
  enableSignalGroupSelection: bool,
  getChipContent: func,
  columnTitles: arrayOf(string),
};

SignalSelection.defaultProps = {
  signals: {},
  showSelectedSignalsOnly: false,
  enableSignalGroupSelection: true,
  getChipContent: null,
  columnTitles: null,
};
